import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const quantitySelector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      // Use public API to update quantity selector
      const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      // Update quantity label if it exists
      this.#updateQuantityLabel(cartQty);

      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        addToCartButtonContainer?.disable();

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity.toString());
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          addToCartButtonContainer?.enable();
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#fetchAndUpdateCartQuantity();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) return;

    // Update the button state
    if (currentAddToCartButton) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        addToCartButtonContainer.disable();
      } else {
        addToCartButtonContainer.enable();
      }

      // Update the add to cart button text and icon
      if (newAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }
    }

    if (this.refs.acceleratedCheckoutButtonContainer) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    // Check if quantity rules are appearing/disappearing (causes layout shift)
    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    if (isQuantityRulesChanging && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (currentProductFormButtons && newProductFormButtons) {
        morph(currentProductFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          newQuantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          newQuantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      const quantityLabel = this.querySelector('.quantity-label');
      const newQuantityLabel = event.detail.data.html.querySelector('.quantity-label');
      this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

      const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
      this.#morphOrUpdateElement(quantityRules, newQuantityRules, addToCartButton);
    }

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}




/* ===== Inky Mimi COD Order Form Integration ===== */
(function() {
    // Detect if this is the Build-a-Box product
    if (window.location.pathname.includes('build-a-box') || document.querySelector('#main-bab-container')) {
        console.log("✅ Build-a-Box detected: product-form.js is now sleeping.");
        return; // This is now inside a function, so it is "legal"
    }


document.addEventListener("DOMContentLoaded", async () => {
  const $ = (s) => document.querySelector(s);
  if (!document.querySelector("#inkyOrder")) return;

  const handle = window.location.pathname.split("/products/")[1]?.split("/")[0];
  if (!handle) return;

  const res = await fetch(`/products/${handle}.js`);
  const product = await res.json();

function setVariant(variant) {
  if (!variant) return;

  const variantInput = document.querySelector("#variantId");
  const unitPriceInput = document.querySelector("#unitPrice");
  const productTitleInput = document.querySelector("#productTitle");

  // 🧩 If form was removed (thank-you message shown), stop immediately
  if (!variantInput || !unitPriceInput || !productTitleInput) return;

  variantInput.value = variant.id;
  unitPriceInput.value = (variant.price / 100).toString();
  productTitleInput.value =
    variant.title && variant.title !== "Default Title"
      ? `${product.title} - ${variant.title}`
      : product.title;
}


  // Default variant
  setVariant(product.variants[0]);

  // Detect variant button clicks
  document.querySelectorAll(".variant-picker__option-button").forEach((btn) => {
    btn.addEventListener("click", () => {
      const text = btn.textContent.trim().toLowerCase();
      const match = product.variants.find((v) =>
        v.title.toLowerCase().includes(text)
      );
      if (match) setVariant(match);
    });
  });

  // Detect ?variant= in URL
  const observer = new MutationObserver(() => {
    const urlVar = new URLSearchParams(location.search).get("variant");
    if (urlVar) {
      const match = product.variants.find((v) => String(v.id) === urlVar);
      if (match) setVariant(match);
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Form submit handler
  const form = document.querySelector("#orderForm");
  if (form) {
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const btn = form.querySelector("button");
      // Show spinner while sending
      const submitBtn = form.querySelector("button[type='submit']") || document.querySelector("#submitOrderBtn");

submitBtn.disabled = true;
submitBtn.innerHTML = `
  <span class="inky-spinner" style="
    display: inline-block;
    width: 18px;
    height: 18px;
    border: 2px solid white;
    border-top-color: transparent;
    border-radius: 50%;
    vertical-align: middle;
    margin-inline-end: 6px;
    animation: spin 0.8s linear infinite;
  "></span> جاري الإرسال...
`;


      const payload = {
        secret: "85g2Qimj2X3RTCGtUOPyQvRbnckyip",
        fullName: $("#name").value.trim(),
        phone: $("#phone").value.replace(/\s+/g, "").trim(),
        address: $("#address").value.trim(),
        city: $("#cityInput").value.trim(),
        wilaya: $("#provinceInput").value.trim(),
        productTitle: $("#productTitle").value,
        product_id: Number($("#productId").value),
        variant_id: Number($("#variantId").value),
        quantity: 1,
        price: Number($("#unitPrice").value),
        shipping_method: document.querySelector(
          "input[name='shippingMethod']:checked"
        ).value,
        // 🧾 Use dynamic price from the summary box instead of static fallback
shipping_price: Number(
  document.querySelector("input[name='shippingMethod']:checked").value === "door"
    ? document.getElementById("doorPrice").textContent
    : document.getElementById("deskPrice").textContent
) || 0,

        notes: ""
      };

      try {
  const res = await fetch("https://soft-smoke-8b68.inkymimicoloring.workers.dev/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload)
  });

  let data = {};
  try {
    data = await res.json();
  } catch (jsonErr) {
  console.error("❌ Invalid JSON from worker:", jsonErr);
  alert("⚠️ مشكلة في الاتصال. حاول مجددًا لاحقًا.");
  submitBtn.disabled = false;
  submitBtn.innerHTML = "✅ اشتري الآن";
submitBtn.disabled = false;

  return;
}

  if (data.success) {
  // Hide the form completely
  const formContainer = document.querySelector("#inkyOrder") || form;
formContainer.innerHTML = `
  <!-- 🌸 Pastel Pink Success Popup -->
  <div id="orderSuccessPopup" class="success-modal" style="display:flex;">
    <div class="success-modal-content">
      <span id="closeSuccessPopup" class="close-popup">&times;</span>
      <h2>✅ تم استلام طلبك!</h2>
      <p>سيتم التواصل معك على الهاتف لتأكيد الطلب.</p>
    </div>
  </div>

  <style>
    .success-modal {
      position: fixed;
      z-index: 9999;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.45);
      display: flex;
      justify-content: center;
      align-items: center;
      animation: fadeIn 0.3s ease-in-out;
    }
    .success-modal-content {
      background: #ffeef5; /* 🌸 pastel pink background */
      border-radius: 16px;
      width: 90%;
      max-width: 400px;
      padding: 28px 22px;
      text-align: center;
      animation: slideUp 0.3s ease;
      position: relative;
      font-family: "Cairo", sans-serif;
      border: 2px solid #7a3e52;
    }
    .success-modal-content h2 {
      color: #47b57c; /* mint green for success */
      font-weight: 700;
      margin-bottom: 10px;
      font-size: 1.5rem;
    }
    .success-modal-content p {
      color:rgb(122, 62, 82);
      font-weight: 700;
      font-size: 1.1rem;
      margin: 6px 0 0;
    }
    .close-popup {
      position: absolute;
      top: 10px;
      right: 16px;
      font-size: 26px;
      color: #999;
      cursor: pointer;
      transition: color 0.2s;
    }
    .close-popup:hover {
      color: #333;
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes slideUp {
      from { transform: translateY(30px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
  </style>
`;

// ✅ Fire Facebook Pixel "Purchase" event (no value or currency)
try {
  if (typeof fbq === "function") {
    fbq("track", "Purchase");
   // console.log("💰 Facebook Purchase event fired (no value/currency)");
  } else {
    console.warn("⚠️ fbq not found — Pixel not yet loaded");
  }
} catch (err) {
  console.error("❌ Error sending Purchase event to Pixel:", err);
}




// ✅ Activate popup close behavior AFTER it's rendered
setTimeout(() => {
  const popup = document.getElementById("orderSuccessPopup");
  const closeIcon = document.getElementById("closeSuccessPopup");

  if (closeIcon) closeIcon.addEventListener("click", () => popup.style.display = "none");
  window.addEventListener("click", (event) => {
    if (event.target === popup) popup.style.display = "none";
  });
}, 100);


   // ✅ Safely stop observer *after* everything is submitted and rendered
  setTimeout(() => {
    if (window.variantObserver) window.variantObserver.disconnect();
  }, 1000);
}
else {
    console.error("⚠️ Worker returned error:", data?.error);
    alert("⚠️ حدث خطأ أثناء إرسال الطلب. الرجاء المحاولة مجددًا.");

    // ✅ restore Buy button
    submitBtn.disabled = false;
    submitBtn.innerHTML = "✅ اشتري الآن";
submitBtn.disabled = false;

  }
} 
catch (networkErr) {
  console.error("🌐 Network error:", networkErr);
  alert("⚠️ فشل الاتصال بالخادم. تأكد من الإنترنت وحاول مجددًا.");

  // ✅ restore Buy button even if network failed
  submitBtn.disabled = false;
  submitBtn.innerHTML = "✅ اشتري الآن";
submitBtn.disabled = false;

}

    });

    const phoneInput = document.querySelector("#phone");
    const phoneError = document.querySelector("#phoneError");

    if (phoneInput) {
  phoneInput.addEventListener("input", (e) => {
  let val = e.target.value.replace(/\D/g, ""); // remove non-digits
      if (val.length > 10) val = val.slice(0, 10);
      const formatted = val.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
      e.target.value = formatted;
      if (val.length >= 2 && !/^0(5|6|7)/.test(val)) {
        phoneError.textContent = "❌ رقم الهاتف يجب أن يبدأ بـ 05 أو 06 أو 07.";
        phoneError.style.display = "block";
        phoneInput.style.borderColor = "#d43030";
      } else {
        phoneError.style.display = "none";
        phoneInput.style.borderColor = "#f3b8c0";
      }
  });
}

    orderForm.addEventListener("submit", (e) => {
      const rawPhone = phoneInput.value.replace(/\s+/g, "");
      phoneInput.value = rawPhone;
    });
  }

  /* ===== End Inky Mimi Dynamic Total ===== */

  const totalDisplay = document.querySelector("#total");
  const wilayaInput = document.querySelector("#provinceInput");
  const methodInputs = document.querySelectorAll("input[name='shippingMethod']");
  const unitPriceInput = document.querySelector("#unitPrice");


  /* ===== Economic Delivery ===== */

  const shippingPrices = {
    adrar:              { door: 1650, desk: 850 },  
    chlef:              { door: 550,  desk: 450 },
    laghouat:           { door: 850, desk: 700 },
    "oum el bouaghi":   { door: 700,  desk: 600 },
    batna:              { door: 700,  desk: 600 },
    bejaia:             { door: 700,  desk: 600 },
    biskra:             { door: 850,  desk: 700 },
    bechar:             { door: 850, desk: 700 },
    blida:              { door: 700,  desk: 600 },
    bouira:             { door: 700,  desk: 600 },
    tamanrasset:        { door: 1600, desk: 1400 },
    tebessa:            { door: 850, desk: 700 },
    tlemcen:            { door: 700,  desk: 600 },
    tiaret:             { door: 700,  desk: 600 },
    "tizi ouzou":       { door: 700,  desk: 600 },
    alger:              { door: 550,  desk: 450 },
    djelfa:             { door: 850, desk: 700 },
    jijel:              { door: 700,  desk: 600 },
    setif:              { door: 700,  desk: 600 },
    saida:              { door: 700,  desk: 600 },
    skikda:             { door: 700,  desk: 600 },
    "sidi bel abbes":   { door: 700,  desk: 600 },
    annaba:             { door: 700,  desk: 600 },
    guelma:             { door: 700,  desk: 600 },
    constantine:        { door: 700,  desk: 600 },
    medea:              { door: 700,  desk: 600 },
    mostaganem:         { door: 500,  desk: 400 },
    "m'sila":           { door: 700,  desk: 600 },
    mascara:            { door: 550,  desk: 450 },
    ouargla:            { door: 850, desk: 700 },
    oran:               { door: 550,  desk: 450 },
    "el bayadh":        { door: 1050, desk: 850 },
    illizi:             { door: 1600, desk: 1400 },
    "bordj bou arreridj":{ door: 700, desk: 600 },
    boumerdes:          { door: 700,  desk: 600 },
    "el taref":         { door: 700, desk: 600 },
    tindouf:            { door: 1600, desk: 1400 },
    tissemsilt:         { door: 700,  desk: 600 },
    "el oued":          { door: 850, desk: 700 },
    khenchela:          { door: 700,  desk: 600 },
    "souk ahras":       { door: 700,  desk: 600 },
    tipaza:             { door: 700,  desk: 600 },
    mila:               { door: 700,  desk: 600 },
    "ain defla":        { door: 700,  desk: 600 },
    naama:              { door: 1050, desk: 850 },
    "ain temouchent":   { door: 700,  desk: 600 },
    ghardaia:           { door: 850, desk: 700 },
    relizane:           { door: 550,  desk: 450 },
    "el m'ghair":       { door: 850, desk: 700 },
    "el meniaa":        { door: 850, desk: 700 },
    "ouled djellal":    { door: 850, desk: 700 },
  //  "bordj badji mokhtar": { door: 1050, desk: 850 },
    "beni abbes":       { door: 850, desk: 750 },
    timimoun:           { door: 1050, desk: 850 },
    touggourt:          { door: 850, desk: 700 },
    djanet:             { door: 1600, desk: 1400 },
    "in salah":         { door: 1600, desk: 1400 },
   // "in guezzam":       { door: 1600, desk: 1400 }
  };


/* ===== Express Delivery ===== 
  const shippingPrices = {
    adrar:              { door: 1050, desk: 850 },
    chlef:              { door: 700,  desk: 550 },
    laghouat:           { door: 950, desk: 750 },
    "oum el bouaghi":   { door: 900,  desk: 650 },
    batna:              { door: 900,  desk: 650 },
    bejaia:             { door: 900,  desk: 650 },
    biskra:             { door: 950,  desk: 750 },
    bechar:             { door: 950, desk: 750 },
    blida:              { door: 900,  desk: 650 },
    bouira:             { door: 900,  desk: 650 },
    tamanrasset:        { door: 1600, desk: 1400 },
    tebessa:            { door: 950, desk: 750 },
    tlemcen:            { door: 900,  desk: 650 },
    tiaret:             { door: 900,  desk: 650 },
    "tizi ouzou":       { door: 900,  desk: 650 },
    alger:              { door: 700,  desk: 500 },
    djelfa:             { door: 950, desk: 750 },
    jijel:              { door: 900,  desk: 650 },
    setif:              { door: 900,  desk: 650 },
    saida:              { door: 900,  desk: 650 },
    skikda:             { door: 900,  desk: 650 },
    "sidi bel abbes":   { door: 900,  desk: 650 },
    annaba:             { door: 900,  desk: 650 },
    guelma:             { door: 900,  desk: 650 },
    constantine:        { door: 900,  desk: 650 },
    medea:              { door: 900,  desk: 650 },
    mostaganem:         { door: 590,  desk: 450 },
    "m'sila":           { door: 900,  desk: 650 },
    mascara:            { door: 700,  desk: 550 },
    ouargla:            { door: 950, desk: 750 },
    oran:               { door: 700,  desk: 550 },
    "el bayadh":        { door: 1050, desk: 850 },
    illizi:             { door: 1600, desk: 1400 },
    "bordj bou arreridj":{ door: 900, desk: 650 },
    boumerdes:          { door: 900,  desk: 650 },
    "el taref":         { door: 900, desk: 650 },
    tindouf:            { door: 1600, desk: 1400 },
    tissemsilt:         { door: 900,  desk: 650 },
    "el oued":          { door: 950, desk: 750 },
    khenchela:          { door: 900,  desk: 650 },
    "souk ahras":       { door: 900,  desk: 650 },
    tipaza:             { door: 900,  desk: 650 },
    mila:               { door: 900,  desk: 650 },
    "ain defla":        { door: 900,  desk: 650 },
    naama:              { door: 1050, desk: 850 },
    "ain temouchent":   { door: 900,  desk: 650 },
    ghardaia:           { door: 950, desk: 750 },
    relizane:           { door: 700,  desk: 550 },
    "el m'ghair":       { door: 950, desk: 750 },
    "el meniaa":        { door: 950, desk: 750 },
    "ouled djellal":    { door: 950, desk: 750 },
  //  "bordj badji mokhtar": { door: 1050, desk: 850 },
    "beni abbes":       { door: 950, desk: 750 },
    timimoun:           { door: 1050, desk: 850 },
    touggourt:          { door: 950, desk: 750 },
    djanet:             { door: 1600, desk: 1400 },
    "in salah":         { door: 1600, desk: 1400 },
   // "in guezzam":       { door: 1600, desk: 1400 }
  };
*/

window.__inkyShippingPrices = shippingPrices; // expose for summary box


  const deskPriceEl = document.querySelector("#deskPrice");
  const doorPriceEl = document.querySelector("#doorPrice");

    // --- Summary box refs (product, shipping, grand total)
  const productPriceEl = document.getElementById("productPrice");
  const shippingCostEl = document.getElementById("shippingCost");
  const totalEl        = document.getElementById("total");

  // Nice money printer
  function setMoney(el, amount) {
    if (!el) return;
    const num = Number(amount) || 0;
    el.textContent = `DZD ${num.toLocaleString()}`;
  }


  // When wilaya changes, update price labels
  provinceInput.addEventListener("change", () => {
    const wilayaKey = (provinceInput.value || "").trim().toLowerCase();
    const map = shippingPrices[wilayaKey];

    if (map) {
      deskPriceEl.textContent = map.desk;
      doorPriceEl.textContent = map.door;
    } else {
      // fallback (if wilaya not found)
      deskPriceEl.textContent = "اختر الولاية";
      doorPriceEl.textContent = "اختر الولاية";
    }

    updateTotal(); // recalc total immediately
  });

  // ===== Wilaya → Commune list (Latin alphabet for testing) =====
  const communes = {
    adrar: ["Adrar", "Akabli", "Aoulef", "Bouda", "Fenoughil", "In Zghmir", "Ouled Ahmed Tammi", "Reggane", "Sali", "Sebaa", "Tamantit", "Tamekten", "Tamest", "Tit", "Tsabit", "Zaouiet Kounta"],
    "ain defla": ["Aïn Benian","Aïn Bouyahia","Aïn Defla","Aïn Lechiekh","Aïn Soltane","Aïn Torki","Arib", "Ben Allal","Bir Ould Khelifa","Birbouche","Bordj Emir Khaled","Boumedfaa","Bourached","Djelida","Djendel","El Abadia","El Amra","El Attaf","Hammam Righa","Hoceinia","Khemis Miliana","Mekhatria","Miliana","Oued Chorfa","Oued Djemaa","Rouina","Sidi Lakhdar","Tacheta Zougagha","Tarik Ibn Ziad","Tiberkanine","Zeddine"],
    "ain temouchent": ["Aghlal","Aïn El Arbaa","Aïn Kihal","Aïn Témouchent","Aïn Tolba","Aoubellil","Beni Saf","Bouzedjar","Chaabat El Leham","Chentouf","El Amria","El Emir Abdelkader","El Malah","El Messaïd","Hammam Bouhadjar","Hassasna","Hassi El Ghella","Oued Berkeche","Oued Sabah","Ouled Boudjemaa","Ouled Kihal","Oulhaça El Gheraba","Sidi Ben Adda","Sidi Boumedienne","Sidi Ouriache","Sidi Safi","Tamzoura","Terga"],
    alger: ["Aïn Benian","Aïn Taya","Alger Centre","Bab El Oued","Bab Ezzouar","Baba Hassen","Bachdjerrah","Baraki","Belouizdad","Ben Aknoun","Beni Messous","Bir Mourad Raïs","Birkhadem","Birtouta","Bologhine","Bordj El Bahri","Bordj El Kiffan","Bourouba","Bouzaréah","Casbah","Chéraga","Dar El Beïda","Dely Ibrahim","Djasr Kasentina","Douera","Draria","El Achour","El Biar","El Hammamet","El Harrach","El Madania","El Magharia","El Marsa","El Mouradia","H’raoua","Hussein Dey","Hydra","Khraicia","Kouba","Les Eucalyptus","Mahelma","Mohammadia","Oued Koriche","Oued Smar","Ouled Chebel","Ouled Fayet","Rahmania","Raïs Hamidou","Reghaïa","Rouïba","Saoula","Sidi M’Hamed","Sidi Moussa","Souidania","Staoueli","Tessala El Merdja","Zeralda"],
    annaba: ["Aïn Berda","Annaba","Berrahal","Chetaïbi","Cheurfa","El Bouni","El Hadjar","Eulma","Oued El Aneb","Seraïdi","Sidi Amar","Treat"],
    batna: ["Abdelkader Azil","Aïn Djasser","Aïn Touta","Aïn Yagout","Arris","Barika","Batna","Ben Foudhala El Hakania","Bitam","Boulhilat","Boumaguer","Boumia (Batna)","Bouzina","Chemora","Chir","Djerma","Djezar","El Hassi","El Madher","Fesdis","Foum Toub","Ghassira","Gosbat","Guigba","Hidoussa","Ichmoul","Inoughissen","Kimmel","Ksar Bellezma","Larbaâ","Lazrou","Lemsane","M’doukel","Maafa","Menaa","Merouana","N’Gaous","Oued Chaaba","Oued El Ma","Oued Taga","Ouled Ammar","Ouled Aouf","Ouled Fadel","Ouled Sellam","Ouled Si Slimane","Ouyoun El Assafir","Rahbat","Ras El Aioun","Sefiane","Seggana","Seriana","T’Kout","Talkhamt","Taxlent","Tazoult","Teniet El Abed","Tighanimine","Tigherghar","Tilatou","Timgad","Zanat El Beida"],
    bechar: ["Abadla","Béchar","Beni Ounif","Boukais","Erg Ferradj","Kenadsa","Lahmar","Mechraa Houari Boumedienne","Meridja","Mougheul","Taghit"],
    bejaia: ["Adekar","Aït Djellil","Aït Ksila","Aït Maouche","Aït Mellikeche","Aït R'zine","Aït Smaïl","Akbou","Akfadou","Amalou","Amizour","Aokas","Barbacha","Béjaïa","Boudjellil","Bouhamza","Boukhelifa","Chellata","Chemini","Darguina","Draâ El Kaïd","El Kseur","Fenaïa Ilmaten","Ferraoun","Ighil Ali","Ighram","Kendira","Kherrata","Leflaye","M'cisna","Melbou","Oued Ghir","Ouzellaguen","Seddouk","Semaoun","Sidi Aïch","Sidi Ayad","Souk El Ténine","Souk Oufella","Tala Hamza","Tamridjet","Taourirt Ighil","Taskriout","Tazmalt","Tibane","Tichy","Tifra","Timezrit","Tinabdher","Toudja"],
    "beni abbes": ["Béni Abbès","Beni Ikhlef","El Ouata","Igli","Kerzaz","Ksabi","Ouled Khoudir","Tamtert","Timoudi"],
    biskra: ["Aïn Naga","Aïn Zaatout","Biskra","Bordj Ben Azzouz","Bouchagroune","Branis","Chetma","Djemorah","El Feidh","El Ghrous","El Hadjeb","El Haouch","El Kantara","El Mizaraa","El Outaya","Foughala","Khenguet Sidi Nadji","Lichana","Lioua","M’chouneche","M’lili","Mekhadma","Oumache","Ourlal","Sidi Okba","Tolga","Zeribet El Oued"],
    blida: ["Aïn Romana","Beni Mered","Beni Tamou","Benkhelil","Blida","Bouarfa","Boufarik","Bougara","Bouinan","Chebli","Chiffa","Chréa","Djebabra","El Affroun","Guerouaou","Hammam Melouane","Larbaa","Meftah","Mouzaïa","Oued Alleug","Oued Djer","Ouled Slama","Ouled Yaïch","Souhane","Soumaa"],
    "bordj bou arreridj": ["Aïn Taghrout","Aïn Tesra","Belimour","Ben Daoud","Bir Kasdali","Bordj Bou Arreridj","Bordj Ghedir","Bordj Zemoura","Colla","Djaafra","El Ach","El Achir","El Anseur","El Hamadia","El M’hir","El Main","Ghilassa","Haraza","Hasnaoua","Khelil","Ksour","Mansoura","Medjana","Ouled Brahem","Ouled Dahmane","Ouled Sidi Brahim","Rabta","Ras El Oued","Sidi Embarek","Taglait","Tassameurt","Tefreg","Teniet En Nasr","Tixter"],
    bouira: ["Aghbalou","Ahl El Ksar","Aïn Bessam","Aïn El Hadjar","Aïn El Turc","Aïn Laloui","Aït Laziz","Aomar","Ath Mansour","Bechloul","Bir Ghbalou","Bordj Okhriss","Bouderbala","Bouira","Boukram","Chorfa","Dechmia","Dirrah","Djebahia","El Adjiba","El Asnam","El Hachimia","El Hakimia","El Khabouzia","El Mokrani","Guerrouma","Hadjera Zerga","Haizer","Hanif","Kadiria","Lakhdaria","M'Chedallah","Maala","Maamora","Mezdour","Oued El Berdi","Ouled Rached","Raouraoua","Ridane","Saharidj","Souk El Khemis","Sour El Ghozlane","Taghzout","Taguedit","Zbarbar"],
    boumerdes: ["Afir","Ammal","Baghlia","Ben Choud","Beni Amrane","Bordj Menaiel","Boudouaou","Boudouaou El Bahri","Boumerdès","Bouzegza Keddara","Chabet El Ameur","Corso","Dellys","Djinet","El Kharrouba","Hammedi","Issers","Khemis El Khechna","Larbatache","Leghata","Naciria","Ouled Aïssa","Ouled Hedadj","Ouled Moussa","Si Mustapha","Sidi Daoud","Souk El Had","Taourga","Thénia","Tidjelabine","Timezrit","Zemmouri"],
    chlef: ["Abou El Hassan","Aïn Merane","Bénairia","Beni Bouateb","Beni Haoua","Beni Rached","Boukadir","Bouzghaia","Breira","Chettia","Chlef","Dahra","El Hadjadj","El Karimia","El Marsa","Harchoun","Harenfa","Labioud Medjadja","Moussadek","Oued Fodda","Oued Goussine","Oued Sly","Ouled Abbes","Ouled Ben Abdelkader","Ouled Fares","Oum Drou","Sendjas","Sidi Abderrahmane","Sidi Akkacha","Sobha","Tadjena","Talassa","Taougrite","Ténès","Zeboudja"],
    constantine: ["Aïn Abid","Aïn Smara","Beni Hamiden","Constantine","Didouche Mourad","El Khroub","Hamma Bouziane","Ibn Badis","Ibn Ziad","Messaoud Boudjeriou","Ouled Rahmoune","Zighoud Youcef"],
    djanet: ["Djanet"],
    djelfa: ["Aïn El Ibel","Aïn Feka","Aïn Maabed","Aïn Oussara","Benhar","Beni Yagoub","Birine","Bouira Lahdab","Charef","Dar Chioukh","Deldoul","Djelfa","Douis","El Guedid","El Idrissia","El Khemis","Had Sahary","Hassi Bahbah","Hassi El Euch","Hassi Fedoul","Messaad","Moudjebara","Sidi Ladjel","Zaccar"],
    "el bayadh": ["Aïn El Orak","Arbaouat","Boualem","Bougtoub","Boussemghoun","Brezina","Chellala","El Abiodh Sidi Cheikh","El Bayadh","El Kheiter","El Mehara","Ghassoul","Kef El Ahmar","Rogassa","Sidi Tifour","Stitten"],
    "el m'ghair": ["Djamaa","El M’Ghair","M’Rara","Oum Touyour","Sidi Amrane","Sidi Khelil","Still","Tendla"],
    "el meniaa": ["El Menia","Hassi Fehal","Hassi Gara"],
    "el oued": ["Bayadha","Beni Guecha","Debila","Douar El Ma","El Ogla","El Oued","Guemar","Hamraia","Hassani Abdelkrim","Hassi Khalifa","Kouinine","Magrane","Mih Ouansa","Nakhla","Oued El Alenda","Ourmas","Reguiba","Robbah","Sidi Aoun","Taghzout","Taleb Larbi","Trifaoui"],
    "el taref": ["Aïn El Assel","Aïn Kerma","Asfour","Ben Mehidi","Berrihane","Besbes","Bouhadjar","Bougous","Bouteldja","Chebaita Mokhtar","Chefia","Chihani","Dréan","Echatt","El Aioun","El Kala","El Tarf","Hammam Beni Salah","Lac des Oiseaux","Oued Zitoun","Raml Souk","Souarekh","Zerizer","Zitouna"],
    ghardaia: ["Berriane","Bounoura","Dhayet Bendhahoua","El Atteuf","El Guerrara","Ghardaïa","Mansoura","Metlili","Sebseb","Zelfana"],
    guelma: ["Aïn Ben Beida","Aïn Larbi","Aïn Makhlouf","Aïn Reggada","Aïn Sandel","Belkheir","Ben Djerrah","Beni Mezline","Bordj Sabath","Bouati Mahmoud","Bouchegouf","Bouhachana","Bouhamdane","Boumahra Ahmed","Dahouara","Djeballah Khemissi","El Fedjoudj","Guellat Bou Sbaa","Guelma","Hammam Debagh","Hammam N’Bail","Héliopolis","Houari Boumédiène","Khezarra","Medjez Amar","Medjez Sfa","Nechmaya","Oued Cheham","Oued Fragha","Oued Zenati","Ras El Agba","Roknia","Sellaoua Announa","Tamlouka"],
    illizi: ["Illizi","In Amenas"],
    "in salah": ["In Salah"],
    jijel: ["Bordj Tahar","Boudriaa Ben Yadjis","Bouraoui Belhadef","Chahna","Chekfa","Djemaâ Beni Habibi","Djimla","El Ancer","El Aouana","El Kennar Nouchfi","El Milia","Emir Abdelkader","Eraguene","Ghebala","Jijel","Kaous","Kheïri Oued Adjoul","Ouadjana","Ouled Rabah","Ouled Yahia Khérouche","Settara","Sidi Abdelaziz","Sidi Maarouf","Taher","Texenna","Ziama Mansouriah"],
    khenchela: ["Aïn Touila","Babar","Baghai","Bouhmama","Chechar","Chelia","El Hamma","El Mahmal","El Ouldja","Ensigha","Kais","Khenchela","M'Sara","M'Toussa","Ouled Rechache","Remila","Tamza","Taouzient","Yabous"],
    laghouat: ["Aflou","Aïn Madhi","Bennasser Benchohra","El Assafia","El Ghicha","El Houaita","Gueltat Sidi Saad","Hassi Delaa","Hassi R'Mel","Kheneg","Ksar El Hirane","Laghouat","Oued M'Zi","Sidi Makhlouf","Tadjemout"],
    "m'sila": ["Aïn El Hadjel","Aïn El Melh","Aïn Errich","Aïn Fares","Aïn Khadra","Belaïba","Ben Srour","Beni Ilmane","Benzouh","Berhoum","Bir Foda","Bou Saâda","Bouti Sayah","Chellal","Dehahna","Djebel Messaâd","El Hamel","El Houamed","Hammam Dhalaa","Khettouti Sed El Djir","Khoubana","M'Cif","M'Sila","M'Tarfa","Maadid","Maarif","Magra","Medjedel","Mohammed Boudiaf","Ouled Addi Guebala","Ouled Atia","Ouled Derradj","Ouled Madhi","Ouled Mansour","Ouled Sidi Brahim","Ouled Slimane","Oultem","Sidi Aïssa","Sidi Ameur","Sidi Hadjeres","Sidi M'Hamed","Slim","Souamaa","Tamsa","Tarmount","Zarzour"],
    mascara: ["Aïn Fares","Aïn Fekan","Aïn Ferah","Aïn Fras","Alaimia","Aouf","Beniane","Bou Hanifia","Bou Henni","Chorfa","El Bordj","El Gaada","El Ghomri","El Guettana","El Keurt","El Menaouer","Ferraguig","Froha","Gharrous","Ghriss","Guerdjoum","Hachem","Hacine","Khalouia","Makdha","Mamounia","Maoussa","Mascara","Matemore","Mocta Douz","Mohammadia","Nesmoth","Oggaz","Oued El Abtal","Oued Taria","Ras El Aïn Amirouche","Sedjerara","Sehailia","Sidi Abdeldjebar","Sidi Abdelmoumen","Sidi Boussaid","Sidi Kada","Sig","Tighennif","Tizi","Zahana","Zelmata"],
    medea: ["Aïn Boucif","Aïssaouia","Aziz","Baata","Benchicao","Beni Slimane","Berrouaghia","Bir Ben Laabed","Boghar","Bou Aiche","Bouaïchoune","Bouchrahil","Boughezoul","Bouskene","Chahbounia","Chellalet El Adhaoura","Derrag","Deux Bassins","Djouab","Draa Essamar","El Azizia","El Guelb El Kebir","El Hamdania","El Omaria","El Ouinet","Hannacha","Kef Lakhdar","Khams Djouamaa","Ksar Boukhari","Médéa","Meftaha","Meghraoua","Mezerana","Mihoub","Moudjbar","Ouamri","Oued Harbil","Ouled Antar","Ouled Bouachra","Ouled Brahim","Ouled Deide","Ouled Hellal","Ouled Maaref","Oum El Djalil","Ouzera","Rebaia","Saneg","Sedraïa","Seghouane","Si Mahdjoub","Sidi Damed","Sidi Errabia","Sidi Naâmanez","Sidi Zahar","Sidi Ziane","Souagui","Tablat","Tafraout","Tamesguida","Tizi Mahdi","Tlatet Eddouar","Zoubiria"],
    mila: ["Ahmed Rachedi","Aïn Beida Harriche","Aïn Mellouk","Aïn Tine","Amira Arrès","Benyahia Abderrahmane","Bouhatem","Chelghoum Laïd","El Mechira","Ferdjioua","Grarem Gouga","Hamala","Mila","Oued Athmania","Oued Endja","Oued Seguen","Ouled Khalouf","Rouached","Sidi Khelifa","Sidi Merouane","Tadjenanet","Teleghma","Yahia Beni Guecha","Zeghaïa"],
    mostaganem: ["Abdelmalek Ramdane","Achaacha","Aïn Boudinar","Aïn Nouïssy","Aïn Sidi Cherif","Aïn Tedles","Blad Touahria","Bouguirat","El Hassaine","Fornaka","Hadjadj","Hassi Mameche","Khadra","Kheireddine","Mansourah","Mazagran","Mesra","Mostaganem","Nekmaria","Oued El Kheir","Ouled Boughalem","Safsaf","Sayada","Sidi Ali","Sidi Lakhdar","Sirat","Souaflia","Sour","Stidia","Tazgait"],
    naama: ["Aïn Ben Khelil","Aïn Sefra","Assela","Djeniene Bourezg","El Biod","Makman Ben Amer","Mecheria","Moghrar","Naâma","Sfissifa","Tiout"],
    oran: ["Aïn El Bia","Aïn El Kerma","Aïn El Turk","Arzew","Ben Freha","Bethioua","Bir El Djir","Bou Sfer","Boufatis","Boutlelis","El Ançor","El Braya","El Kerma","Es Senia","Gdyel","Hassi Ben Okba","Hassi Bounif","Hassi Mefsoukh","Marsat El Hadjadj","Mers El Kébir","Misserghin","Oran","Oued Tlelat","Sidi Benyebka","Sidi Chami","Tafraoui"],
    ouargla: ["Aïn Beida","Hassi Ben Abdellah","Hassi Messaoud","N'Goussa","Ouargla","Rouissat","Sidi Khouiled"],
    "ouled djellal": ["Doucen","Ech Chaïba","Ouled Djellal","Sidi Khaled"],
    "oum el bouaghi": ["Aïn Babouche","Aïn Beïda","Aïn Diss","Aïn Fakroun","Aïn Kercha","Aïn M'lila","Aïn Zitoun","Behir Chergui","Berriche","Bir Chouhada","Dhalaa","El Amiria","El Belala","El Djazia","El Fedjoudj Boughrara Saoudi","El Harmilia","Fkirina","Hanchir Toumghani","Ksar Sbahi","Meskiana","Oued Nini","Ouled Gacem","Ouled Hamla","Ouled Zouaï","Oum El Bouaghi","Rahia","Sigus","Souk Naâmane","Zorg"],
    relizane: ["Aïn Rahma","Aïn Tarek","Ammi Moussa","Belassel Bouzegza","Bendaoud","Beni Dergoun","Beni Zentis","Dar Ben Abdellah","Djidouia","El Guettar","El Hamadna","El Hassi","El Matmar","El Ouldja","Had Echkalla","Hamri","Kalaa","Lahlef","Mazouna","Mediouna","Mendes","Merdja Sidi Abed","Ouarizane","Oued El Djemaa","Oued Essalem","Oued Rhiou","Ouled Aiche","Ouled Sidi Mihoub","Ramka","Relizane","Sidi Khettab","Sidi Lazreg","Sidi M'Hamed Ben Ali","Sidi M'Hamed Benaouda","Sidi Saada","Souk El Had","Yellel","Zemmora"],
    saida: ["Aïn El Hadjar","Aïn Soltane","Doui Thabet","El Hassasna","Maamora","Moulay Larbi","Ouled Brahim","Ouled Khaled","Saïda","Sidi Ahmed","Sidi Amar","Sidi Boubekeur","Tircine","Youb"],
    setif: ["Aïn Abessa","Aïn Arnat","Aïn Azel","Aïn El Kebira","Aïn Lahdjar","Aïn Legradj","Aïn Oulmene","Aïn Roua","Aïn Sebt","Aït Naoual Mezada","Aït Tizi","Aït Wertilan","Amoucha","Babor","Bazer Sakhra","Beidha Bordj","Belaa","Beni Aziz","Beni Chebana","Beni Fouda","Beni Hocine","Beni Mouhli","Bir El Arch","Bir Haddada","Bouandas","Bougaa","Bousselam","Boutaleb","Dehamcha","Djemila","Draa Kebila","El Eulma","El Ouldja","El Ouricia","Guellal","Guelta Zerka","Guenzet","Guidjel","Hamma","Hammam Guergour","Hammam Soukhna","Harbil","Ksar El Abtal","Maaouia","Maoklane","Mezloug","Oued El Barad","Ouled Addouane","Ouled Sabor","Ouled Si Ahmed","Ouled Tebben","Rasfa","Salah Bey","Serdj El Ghoul","Sétif","Tachouda","Talaifacene","Taya","Tella","Tizi N'Bechar"],
    "sidi bel abbes": ["Aïn Adden","Aïn El Berd","Aïn Kada","Aïn Thrid","Aïn Tindamine","Amarnas","Badredine El Mokrani","Belarbi","Ben Badis","Benachiba Chelia","Bir El Hammam","Boudjebaa El Bordj","Boukhanefis","Chettouane Belaila","Dhaya","El Haçaiba","Hassi Dahou","Hassi Zehana","Lamtar","M'CId","Makedra","Marhoum","Merine","Mezaourou","Mostefa Ben Brahim","Moulay Slissen","Oued Sebaa","Oued Sefioun","Oued Taourira","Ras El Ma","Redjem Demouche","Sehala Thaoura","Sfissef","Sidi Ali Benyoub","Sidi Ali Boussidi","Sidi Bel Abbès","Sidi Brahim","Sidi Chaib","Sidi Daho des Zairs","Sidi Hamadouche","Sidi Khaled","Sidi Lahcene","Sidi Yacoub","Tabia","Tafissour","Taoudmout","Teghalimet","Telagh","Tenira","Tessala","Tilmouni","Zerouala"],
    skikda: ["Aïn Bouziane","Aïn Charchar","Aïn Kechra","Aïn Zouit","Azzaba","Bekkouche Lakhdar","Ben Azzouz","Beni Bechir","Beni Oulbane","Beni Zid","Bin El Ouiden","Bouchtata","Cheraïa","Collo","Djendel Saadi Mohamed","El Ghedir","El Hadaïek","El Harrouch","El Marsa","Emdjez Edchich","Es Sebt","Filfila","Hamadi Krouma","Kanoua","Kerkera","Kheneg Mayoum","Oued Zehour","Ouldja Boulballout","Ouled Attia","Ouled Hbaba","Oum Toub","Ramdane Djamel","Salah Bouchaour","Sidi Mezghiche","Skikda","Tamalous","Zerdaza","Zitouna"],
    "souk ahras": ["Aïn Zana","Bir Bou Haouch","Drea","Hanencha","Heddada","Khedara","Khemissa","M'daourouch","Mechroha","Merahna","Ouillen","Ouled Driss","Ragouba","Sedrata","Sidi Fredj","Souk Ahras","Taoura","Tiffech","Zaarouria","Zouabi"],
    tamanrasset: ["Tamanrasset"],
    tebessa: ["Aïn Zerga","Bedjene","Bekkaria","Bir Dheheb","Bir Mokkadem","Boukhdra","Boulhaf Dir","Cheria","El Aouinet","El Houidjbet","El Kouif","El Ma Labiadh","El Meridj","El Mezerraa","El Ogla","Ferkane","Guorriguer","Hammamet","Morsott","Negrine","Ogla Melha","Ouenza","Oum Ali","Safsaf El Ouesra","Stah Guentis","Tébessa","Tlidjene"],
    tiaret: ["Aïn Bouchekif","Aïn Deheb","Aïn Dzarit","Aïn El Hadid","Aïn Kermes","Bougara","Chehaima","Dahmouni","Djebilet Rosfa","Djillali Ben Amar","Faidja","Frenda","Guertoufa","Hamadia","Ksar Chellala","Mahdia","Mechraa Safa","Medrissa","Medroussa","Meghila","Mellakou","Naima","Oued Lilli","Rahouia","Rechaiga","Sebaine","Sebt","Serghine","Si Abdelghani","Sidi Abderrahmane","Sidi Ali Mellal","Sidi Hosni","Sougueur","Tagdemt","Takhemaret","Tiaret","Tidda","Tousnina","Zmalet El Emir Abdelkader"],
    timimoun: ["Aougrout","Charouine","Deldoul","Ksar Kaddour","Metarfa","Ouled Aïssa","Ouled Saïd","Talmine","Timimoun","Tinerkouk"],
    tindouf: ["Tindouf"],
    tipaza: ["Aghbal","Ahmar El Aïn","Aïn Tagourait","Attatba","Beni Milleuk","Bou Ismaïl","Bouharoun","Bourkika","Chaïba","Cherchell","Damous","Douaouda","Fouka","Gouraya","Hadjeret Ennous","Hadjout","Khemisti","Koléa","Larhat","Menaceur","Merad","Messelmoun","Nador","Sidi Amar","Sidi Ghiles","Sidi Rached","Sidi Semiane","Tipaza"],
    tissemsilt: ["Ammari","Bordj Bou Naama","Bordj El Emir Abdelkader","Boucaid","Khemisti","Lardjem","Layoune","Lazharia","Maacem","Melaab","Ouled Bessem","Sidi Lantri","Sidi Slimane","Tamalaht","Theniet El Had","Tissemsilt","Youssoufia"],
    "tizi ouzou": ["Abi Youcef","Aghribs","Agouni Gueghrane","Aïn El Hammam","Aïn Zaouia","Aït Aggouacha","Aït Aïssa Mimoun","Aït Bouadou","Aït Boumahdi","Aït Chafaa","Aït Khellili","Aït Mahmoud","Aït Oumalou","Aït Toudert","Aït Yahia","Aït Yahia Moussa","Aït Yenni","Akbil","Akerrou","Assi Youcef","Ath Zikki","Azazga","Azeffoun","Beni Aïssi","Beni Douala","Beni Zmenzer","Boghni","Boudjima","Bounouh","Bouzguen","Draâ Ben Khedda","Draâ El Mizan","Freha","Frikat","Iboudraren","Idjeur","Iferhounène","Iflissen","Ifigha","Illilten","Illoula Oumalou","Imsouhel","Irdjen","Larbaâ Nath Irathen","M'Kira","Maatkas","Makouda","Mechtras","Mekla","Mizrana","Ouacif","Ouadhia","Ouaguenoun","Sidi Namane","Souamaâ","Souk El Thenine","Tadmaït","Tigzirt","Timizart","Tirmitine","Tizi Gheniff","Tizi N'Tleta","Tizi Ouzou","Tizi Rached","Yakouren","Yatafen"],
    tlemcen: ["Aïn Fetah","Aïn Fezza","Aïn Ghoraba","Aïn Kebira","Aïn Nehala","Aïn Tallout","Aïn Youcef","Amieur","Azails","Bab El Assa","Beni Bahdel","Beni Boussaid","Beni Khellad","Beni Mester","Beni Quarsous","Beni Semiel","Beni Snous","Bensekrane","Bouhlou","Chetouane","Dar Yaghmouracene","Djebala","El Aricha","El Bouihi","El Fehoul","El Gor","Fellaoucene","Ghazouet","Hammam Boughrara","Hennaya","Honaïne","Maghnia","Mansourah","Marsa Ben M’Hidi","Msirda Fouaga","Nedroma","Oued Lakhdar","Ouled Mimoun","Ouled Riyah","Remchi","Sabra","Sebaa Chioukh","Sebdou","Sidi Abdelli","Sidi Djillali","Sidi Medjahed","Souahlia","Souani","Souk Tlata","Terny Beni Hdiel","Tienet","Tlemcen","Zenata"],
    touggourt: ["Benaceur","Blidet Amor","M'Naguer","Megarine","Nezla","Sidi Slimane","Taibet","Tamacine","Tebesbest","Touggourt","Zaouia El Abidia"],
  };

  // Wilaya and commune dropdown logic
  const wilayaSelect = document.querySelector("#provinceInput");
  const communeSelect = document.querySelector("#cityInput");

if (wilayaSelect && communeSelect) {
  wilayaSelect.addEventListener("change", () => {
    const selected = wilayaSelect.value.trim().toLowerCase();
    communeSelect.innerHTML = "";

    // 🩷 Add default placeholder first
    const defaultOpt = document.createElement("option");
    defaultOpt.value = "";
    defaultOpt.textContent = "Baladia";
    defaultOpt.disabled = true;
    defaultOpt.selected = true;
    communeSelect.appendChild(defaultOpt);

    // 🏙️ If wilaya has communes, list them
    if (communes[selected] && communes[selected].length > 0) {
      communes[selected].forEach((commune) => {
        const opt = document.createElement("option");
        opt.value = commune;
        opt.textContent = commune;
        communeSelect.appendChild(opt);
      });
      communeSelect.disabled = false;
    } else {
      // If wilaya not found or empty → keep disabled
      communeSelect.disabled = true;
    }

    // Reset selection visually
    communeSelect.value = "";
  });
}


  

function updateTotal() {
  const wilayaKey = (wilayaInput.value || "").trim().toLowerCase();
  const method = document.querySelector("input[name='shippingMethod']:checked")?.value;
  const unitPrice = Number(unitPriceInput.value) || 0;

  const productPriceEl = document.getElementById("productPrice");
  const shippingCostEl = document.getElementById("shippingCost");
  const totalDisplay = document.getElementById("total");

  // 🟣 If no wilaya selected, set shipping to 0
  if (!wilayaKey) {
  // 🩷 Reset visible shipping radio prices
  const deskPriceEl = document.querySelector("#deskPrice");
  const doorPriceEl = document.querySelector("#doorPrice");

  if (deskPriceEl) deskPriceEl.textContent = "اختر الولاية";
  if (doorPriceEl) doorPriceEl.textContent = "اختر الولاية";

  // 🩷 Reset summary box
  const unitPrice = Number(unitPriceInput.value) || 0;
  productPriceEl.textContent = `DZD ${unitPrice.toLocaleString()}`;
  shippingCostEl.textContent = `اختر الولاية`;
  totalDisplay.textContent = `DZD ${unitPrice.toLocaleString()}`;

  return;
}



// 🟣 Normalize wilaya and find matching price (handles every apostrophe variant)
let wilayaVal = (wilayaInput.value || "").trim().toLowerCase();

// Decode HTML entities like M&#39;sila or M&amp;#39;ghair
wilayaVal = wilayaVal.replace(/&#39;|&apos;|&amp;#39;/g, "'");

// Remove accents and normalize spacing + apostrophes
const normalizedWilaya = wilayaVal
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")   // accents
  .replace(/['’`ʹ‘‛‵ʽˊˋ]/g, "")      // 🟣 all quote variants ever used
  .replace(/[-_]+/g, " ")
  .replace(/\s+/g, " ")
  .trim()
  .toLowerCase();

// Compare safely with normalized shipping keys
const map = Object.keys(shippingPrices).find((k) => {
  const cleanKey = k
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/['’`ʹ‘‛‵ʽˊˋ]/g, "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
  return cleanKey === normalizedWilaya;
});

// Default to 0 if not found
const prices = map ? shippingPrices[map] : { door: 0, desk: 0 };
const shipping = method === "door" ? prices.door : prices.desk;


  // 🟣 Update all summary fields
  productPriceEl.textContent = `DZD ${unitPrice.toLocaleString()}`;
  shippingCostEl.textContent = `DZD ${shipping.toLocaleString()}`;
  totalDisplay.textContent = `DZD ${(unitPrice + shipping).toLocaleString()}`;
}



  wilayaInput.addEventListener("input", updateTotal);
  methodInputs.forEach(r => r.addEventListener("change", updateTotal));

  updateTotal();
});

const style = document.createElement("style");
style.innerHTML = `
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
`;
document.head.appendChild(style);

})();